import { logger } from 'features/logger/logger';
import { getAssetUrlWithTempLocationSupport } from 'features/questionnaires/store/questionnaireAssetApiSlice';
import type { DbProtocolManifest } from '@hub/common';

// Assets are static and cached, so each URL is fetched at most once per session
// even as the schedule recomputes. A failed fetch is removed so it can retry.
const preloadedUrls = new Set<string>();

// Warms the browser/WebView cache with the participation manifest's images.
// The questionnaire's CDN assets are not bundled (unlike /public), so without
// this a task opened offline on native — or first-opened on web — can miss
// images. The DSL is warmed by useParticipationSchedule and the stylesheet by
// the participation guard (useProtocolStylesheet).
// Fire-and-forget: failures are logged, never thrown.
export const preloadParticipationAssets = (
  manifest: DbProtocolManifest
): void => {
  const urls = Object.values(manifest.images ?? {}).map(imageUrl =>
    getAssetUrlWithTempLocationSupport(imageUrl)
  );

  for (const url of urls) {
    if (preloadedUrls.has(url)) {
      continue;
    }
    preloadedUrls.add(url);

    void fetch(url)
      .then(() => {
        logger.debug(`Preloaded participation asset: ${url}`);
      })
      .catch((error: unknown) => {
        preloadedUrls.delete(url);
        logger.error(
          `Unable to preload participation asset: ${url}`,
          undefined,
          error as Error
        );
      });
  }
};
